import React, { useCallback, useEffect, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Spinner, Code, Divider,
  Button, Collapse, Tooltip, Switch, useToast,
} from "@chakra-ui/react";
import {
  readCache, writeCache, formatAge, PHI_DEID_TESTS_KEY,
} from "../../utils/resultCache";

/**
 * Test results survive closing the modal and the tab. Each run costs a real API
 * call, so a cached result is shown with its age and every Test button re-runs.
 * Stored under one key as { [pathId]: { result, savedAt } }.
 */
function readAllCachedTests() {
  return readCache(PHI_DEID_TESTS_KEY)?.data ?? {};
}

function cacheTestResult(pathId, result) {
  const all = readAllCachedTests();
  all[pathId] = { result, savedAt: new Date().toISOString() };
  writeCache(PHI_DEID_TESTS_KEY, all);
}

function StatusBanner({ status }) {
  if (status.healthy) {
    return (
      <Alert status="success" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Active — patient details are replaced before every AI call</Text>
          <Text fontSize="sm">Detector: {status.detector}</Text>
        </Box>
      </Alert>
    );
  }
  if (!status.detector_available && status.enforcing) {
    const repair = status.repair || {};
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">
            {repair.repairing ? "AI is blocked — repairing automatically" : "AI is blocked"}
          </Text>
          <Text fontSize="sm">
            The name detector could not load, so AI calls are refused rather than
            sending patient data.
            {repair.repairing
              ? ` The server keeps retrying on its own (attempt ${repair.attempts}`
                + `${repair.next_try_in != null ? `, next in ${repair.next_try_in}s` : ""}); `
                + "AI requests made meanwhile wait up to 30 s for it."
              : ""}
            {" "}If it does not recover, run <Code fontSize="xs">pip install -r requirements.txt</Code>{" "}
            and <Code fontSize="xs">python -m spacy download en_core_web_sm</Code>, then restart.
          </Text>
          {repair.last_error && (
            <Text fontSize="xs" color="gray.600" mt={1}>Last error: {repair.last_error}</Text>
          )}
        </Box>
      </Alert>
    );
  }
  return (
    <Alert status="warning" borderRadius="md">
      <AlertIcon />
      <Box>
        <Text fontWeight="bold">Not enforced — patterns only</Text>
        <Text fontSize="sm">
          DEID_ENFORCE is off. Names without a title or label will reach the model.
        </Text>
      </Box>
    </Alert>
  );
}

function Demo({ demo }) {
  if (!demo || demo.error) {
    return <Text fontSize="sm" color="red.600">Self-test failed: {demo?.error}</Text>;
  }
  return (
    <Box>
      <HStack mb={2}>
        <Text fontSize="lg" fontWeight="bold">What the AI actually receives</Text>
        <Badge colorScheme="blue">{demo.replacements} replaced</Badge>
        {demo.dates_withheld > 0 && (
          <Badge colorScheme="purple">{demo.dates_withheld} date{demo.dates_withheld === 1 ? "" : "s"} withheld</Badge>
        )}
        <Badge colorScheme={demo.restored_ok ? "green" : "red"}>
          {demo.restored_ok ? "round trip verified" : "ROUND TRIP FAILED"}
        </Badge>
      </HStack>
      <VStack align="stretch" spacing={2}>
        <Box bg="red.50" borderLeftWidth="4px" borderLeftColor="red.400"
             borderRadius="md" px={3} py={2}>
          <Text fontSize="xs" fontWeight="bold" color="red.700" mb={1}>
            Your data
          </Text>
          <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap">{demo.before}</Code>
        </Box>
        <Box bg="green.50" borderLeftWidth="4px" borderLeftColor="green.400"
             borderRadius="md" px={3} py={2}>
          <Text fontSize="xs" fontWeight="bold" color="green.700" mb={1}>
            Sent to the model
          </Text>
          <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap">{demo.after}</Code>
        </Box>
      </VStack>
      <Text fontSize="sm" color="gray.600" mt={2}>
        The real values are put back into the model's answer before anyone sees it.
      </Text>
    </Box>
  );
}

function Stage({ title, scheme, children, note }) {
  return (
    <Box bg={`${scheme}.50`} borderLeftWidth="4px" borderLeftColor={`${scheme}.400`}
         borderRadius="md" px={3} py={2}>
      <Text fontSize="xs" fontWeight="bold" color={`${scheme}.700`} mb={1}>
        {title}
      </Text>
      {children}
      {note && <Text fontSize="xs" color="gray.600" mt={1}>{note}</Text>}
    </Box>
  );
}

/** What a row's result means for the summary: passed | unprotected | failed. */
function verdictOutcome(verdict) {
  if (verdict?.passed === true) return "passed";
  if (verdict?.status === "unprotected") return "unprotected";
  return "failed";
}

function verdictTone(verdict) {
  if (!verdict) return { scheme: "gray", label: "—", headline: "" };
  if (verdict.status === "unprotected") {
    return {
      scheme: "orange",
      label: "NOT PROTECTED",
      headline: "PHI reaches this model — de-identification cannot apply here",
    };
  }
  if (verdict.status === "switched_off") {
    // Not a limitation like images or audio: a setting in our own code is
    // off, so the chart went to the model as written. Red, and the check
    // below names the setting to change.
    return {
      scheme: "red",
      label: "FAIL",
      headline: "De-identification is switched OFF for this path — PHI reached the model",
    };
  }
  return verdict.passed
    ? { scheme: "green", label: "PASS",
        headline: "No PHI reached the AI, and the answer is still correct" }
    : { scheme: "red", label: "FAIL", headline: "Something is wrong — see below" };
}

function Verdict({ verdict }) {
  if (!verdict) return null;
  const { scheme, label, headline } = verdictTone(verdict);
  return (
    <Box bg={`${scheme}.50`} borderWidth="2px" borderColor={`${scheme}.400`}
         borderRadius="md" px={3} py={2}>
      <HStack mb={verdict.checks?.length ? 1 : 0}>
        <Badge colorScheme={scheme} fontSize="sm" px={2} data-testid="verdict-badge">
          {label}
        </Badge>
        <Text fontSize="sm" fontWeight="bold" color={`${scheme}.800`}>{headline}</Text>
      </HStack>
      <VStack align="stretch" spacing={0.5}>
        {(verdict.checks || []).map((c) => (
          <HStack key={c.name} fontSize="xs" align="baseline">
            <Text color={c.passed ? "green.600" : "red.600"} fontWeight="bold">
              {c.passed ? "✓" : "✕"}
            </Text>
            <Text>{c.name}</Text>
            {c.detail && <Text color="red.700">— {c.detail}</Text>}
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}

function Goal({ task }) {
  if (!task) return null;
  return (
    <Box bg="gray.50" borderLeftWidth="4px" borderLeftColor="gray.400"
         borderRadius="md" px={3} py={2}>
      <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
        WHAT THE AI WAS ASKED TO DO
      </Text>
      <Text fontSize="sm" mb={2}>{task.goal}</Text>
      <Text fontSize="xs" fontWeight="bold" color="gray.600">Instruction</Text>
      <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap" display="block" mb={1}>
        {task.system}
      </Code>
      {task.question && (
        <>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">Question</Text>
          <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap" display="block">
            {task.question}
          </Code>
        </>
      )}
    </Box>
  );
}

function StageNote({ note }) {
  return (
    <Box bg="gray.100" borderLeftWidth="4px" borderLeftColor="gray.400"
         borderRadius="md" px={3} py={2}>
      <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>{note.stage}</Text>
      <Text fontSize="sm" color="gray.700">{note.note}</Text>
    </Box>
  );
}

function ServerLog({ lines }) {
  // Collapsed by default: five wide lines per call drown the trace above them,
  // and the trace is the point. The log is for when you want the raw record.
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button size="xs" variant="link" colorScheme="gray"
              data-testid="toggle-server-log" onClick={() => setOpen((v) => !v)}>
        {open ? "Hide" : "Show"} server log ([HIPPAA] · {lines.length} lines)
      </Button>
      <Collapse in={open} animateOpacity unmountOnExit>
        <Box bg="gray.800" borderRadius="md" p={3} mt={1} overflowX="auto">
          {lines.map((ln, i) => (
            <Code
              key={i}
              display="block"
              whiteSpace="pre"
              fontSize="10px"
              bg="transparent"
              color="green.200"
              // A blank line between stages: five wide lines run together
              // otherwise, and the stage boundary is what you scan for.
              mb={i < lines.length - 1 ? 3 : 0}
            >
              {ln}
            </Code>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function Trace({ result }) {
  const t = result.trace;
  const notes = t.stage_notes || [];
  const noteFor = (stage) => notes.find((n) => n.stage.startsWith(stage));
  const unprotected = ["unprotected", "switched_off"].includes(result.verdict?.status);
  return (
    <VStack align="stretch" spacing={2} mt={3} mb={1}>
      <Verdict verdict={result.verdict} />
      <Goal task={result.task} />
      <Alert status="info" fontSize="xs" borderRadius="md">
        <AlertIcon />
        Synthetic patient — invented data, so the withheld values are safe to show
        here. The server log never prints them.
      </Alert>

      {noteFor("1") ? <StageNote note={noteFor("1")} /> : (
        <Stage title="1 · WHAT WENT IN" scheme="red">
          <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap">{t.sent_in}</Code>
        </Stage>
      )}

      {noteFor("2") ? <StageNote note={noteFor("2")} /> : (
      <Stage
        title={`2 · WITHHELD (${t.withheld.length})`}
        scheme="purple"
        note={t.dates_withheld
          ? `Every PHI date is withheld and put back by our code — the AI is told how far each one is from today, and a person's age instead of their date of birth. It is never told today's calendar date. Only reference dates (a guideline, a form version) are sent as written.`
          : null}
      >
        <VStack align="stretch" spacing={0.5}>
          {t.withheld.map((w) => (
            <HStack key={w.token} fontSize="xs" spacing={2}>
              <Code fontSize="xs" colorScheme="purple">{w.token}</Code>
              <Text>←</Text>
              <Text fontWeight="medium">{w.value}</Text>
            </HStack>
          ))}
        </VStack>
      </Stage>
      )}

      {noteFor("3") ? <StageNote note={noteFor("3")} /> : (
        <Stage title="3 · WHAT THE AI RECEIVED" scheme={unprotected ? "orange" : "green"}>
          <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap">{t.prompt_to_ai}</Code>
        </Stage>
      )}

      {noteFor("4") ? <StageNote note={noteFor("4")} /> : (
        <Stage
          title="4 · WHAT THE AI SENT BACK"
          scheme={unprotected ? "red" : "orange"}
          note={unprotected
            ? "Real patient details, because none were withheld."
            : "Still carrying placeholders — the model never saw the real values."}
        >
          <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap">
            {t.ai_response_raw || "(nothing captured)"}
          </Code>
        </Stage>
      )}

      {t.leaked_values?.length > 0 && (
        <Alert status="error" fontSize="xs" borderRadius="md">
          <AlertIcon />
          Reached the model unprotected: {t.leaked_values.join(", ")}
        </Alert>
      )}

      {noteFor("5") ? <StageNote note={noteFor("5")} /> : (
      <Stage
        title="5 · FINAL ANSWER, PHI PUT BACK"
        scheme={t.placeholders_left ? "red" : "blue"}
        note={t.placeholders_left
          ? `${t.placeholders_left} placeholder(s) survived into the answer — a bug.`
          : "No placeholders left."}
      >
        <Code fontSize="xs" bg="transparent" whiteSpace="pre-wrap">
          {t.final_answer || "(empty)"}
        </Code>
      </Stage>
      )}

      {result.error && (
        <Alert status="error" fontSize="xs"><AlertIcon />{result.error}</Alert>
      )}

      {result.logs?.length > 0 && <ServerLog lines={result.logs} />}
    </VStack>
  );
}

/**
 * The Master Note row's switch — for the schema the dashboard is being viewed
 * in. It writes schema_settings.settings.DEID_MASTER_NOTE for that schema; the
 * row redraws from what the server says is stored, never from the click alone.
 */
function SchemaSwitch({ path, setMasterNoteDeid, onPaths }) {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const schema = path.schema || "public";
  const enabled = Boolean(path.enabled);
  const flip = async (next) => {
    if (!setMasterNoteDeid) return;
    setPending(true);
    try {
      const r = await setMasterNoteDeid(schema, next);
      if (r?.paths) onPaths?.(r.paths);
      toast({
        title: `${schema}: de-identification ${r?.enabled ? "ON" : "OFF"}`,
        description: r?.where,
        status: r?.enabled ? "success" : "warning",
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      toast({ title: `Could not change ${schema}`, description: e.message,
              status: "error", duration: 6000, isClosable: true });
    } finally {
      setPending(false);
    }
  };
  return (
    <HStack
      mt={2}
      spacing={3}
      px={2}
      py={1}
      borderWidth="1px"
      borderRadius="md"
      borderColor={enabled ? "green.200" : "red.300"}
      bg={enabled ? "green.50" : "red.50"}
      display="inline-flex"
      title={path.where}
      data-testid="deid-schema-switch"
    >
      <Switch
        size="sm"
        colorScheme="green"
        isChecked={enabled}
        isDisabled={pending || !setMasterNoteDeid}
        onChange={(e) => flip(e.target.checked)}
        aria-label={`Master Note de-identification for ${schema}`}
        data-testid={`deid-switch-${schema}`}
      />
      <Text fontSize="xs" fontWeight="bold">{schema}</Text>
      <Badge colorScheme={enabled ? "green" : "red"} fontSize="2xs"
             data-testid={`deid-state-${schema}`}>
        {enabled ? "ON" : "OFF"}
      </Badge>
      <Text fontSize="xs" color="gray.600">
        schema_settings.settings.DEID_MASTER_NOTE (no key = on)
      </Text>
    </HStack>
  );
}

function PathRow({ path, runTest, runAllToken, onVerdict, cachedEntry,
                   setMasterNoteDeid, onPaths }) {
  // Orange is for paths that CANNOT be covered (images, audio). A path that
  // could be and is switched off by a clinic's setting is red — the same grade
  // its test gets.
  const scheme = path.covered ? "green" : path.switched_off ? "red" : "orange";
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(cachedEntry?.result ?? null);
  const [ranAt, setRanAt] = useState(cachedEntry?.savedAt ?? null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // autoExpand: pressing this row's own Test button is a request to see it.
  // A Test All sweep is not — that should leave a scannable list of badges.
  const onTest = useCallback(async (autoExpand) => {
    setBusy(true);
    setError(null);
    try {
      const r = await runTest(path.id);
      setResult(r);
      setRanAt(new Date().toISOString());
      cacheTestResult(path.id, r);
      setExpanded(Boolean(autoExpand));
      onVerdict?.(path.id, verdictOutcome(r.verdict));
    } catch (e) {
      setError(e.message);
      setExpanded(Boolean(autoExpand));
      onVerdict?.(path.id, "failed");
    } finally {
      setBusy(false);
    }
  }, [path.id, runTest, onVerdict]);

  // Test All bumps a token; every row runs itself and stays collapsed.
  useEffect(() => {
    if (runAllToken > 0) onTest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runAllToken]);

  return (
    <Box borderBottomWidth="1px" borderColor="gray.100" py={3}>
      <HStack justify="space-between" align="flex-start">
        <Box flex="1" minW={0}>
          <HStack spacing={3} align="baseline" flexWrap="wrap">
            <Badge colorScheme={scheme} fontSize="xs" data-testid={`row-coverage-${path.id}`}>
              {path.covered ? "COVERED" : path.switched_off ? "SWITCHED OFF" : "NOT COVERED"}
            </Badge>
            <Text fontSize="md" fontWeight="medium">{path.name}</Text>
            <Text fontSize="xs" color="gray.500">{path.via}</Text>
            {result?.verdict && (
              <Badge
                colorScheme={verdictTone(result.verdict).scheme}
                fontSize="xs"
                data-testid={`row-verdict-${path.id}`}
              >
                {verdictTone(result.verdict).label}
              </Badge>
            )}
            {result && (
              <Button
                size="xs"
                variant="link"
                colorScheme="blue"
                data-testid={`details-${path.id}`}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Hide details \u25b4" : "Details \u25be"}
              </Button>
            )}
            {result && ranAt && !busy && (
              <Text fontSize="xs" color="gray.500" data-testid={`ran-at-${path.id}`}>
                {formatAge(ranAt)}
              </Text>
            )}
          </HStack>
          {path.why && (
            <Text fontSize="sm" color={path.switched_off ? "red.700" : "orange.700"} mt={0.5}>
              {path.why}
            </Text>
          )}
          {path.id === "master_note" && (
            <SchemaSwitch path={path} setMasterNoteDeid={setMasterNoteDeid} onPaths={onPaths} />
          )}
        </Box>
        <Tooltip
          label="Shows what actually happens on this path — including the steps where
                 nothing is withheld"
          isDisabled={path.covered}
          hasArrow
        >
          <Button
            size="sm"
            variant="outline"
            colorScheme={path.covered ? "blue" : path.switched_off ? "red" : "orange"}
            flexShrink={0}
            isLoading={busy}
            data-testid={`test-${path.id}`}
            onClick={() => onTest(true)}
          >
            {result ? "Re-test" : "Test"}
          </Button>
        </Tooltip>
      </HStack>

      {error && (
        <Alert status="error" fontSize="xs" mt={2}><AlertIcon />{error}</Alert>
      )}
      <Collapse in={Boolean(result) && expanded} animateOpacity unmountOnExit>
        {result && <Trace result={result} />}
      </Collapse>

    </Box>
  );
}

export default function PhiDeidPanel({ load, runTest, setMasterNoteDeid }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [runAllToken, setRunAllToken] = useState(0);
  // Seeded from cache so the summary reflects previously-run tests immediately,
  // rather than reading "0 tested" beside rows that plainly have results.
  const [cachedTests] = useState(readAllCachedTests);
  const [verdicts, setVerdicts] = useState(() => {
    const seeded = {};
    Object.entries(readAllCachedTests()).forEach(([id, entry]) => {
      seeded[id] = verdictOutcome(entry?.result?.verdict);
    });
    return seeded;
  });

  // Each row reports its own outcome up ("passed" | "unprotected" | "failed"),
  // so the summary counts real results rather than re-running anything.
  const onVerdict = useCallback((pathId, outcome) => {
    setVerdicts((prev) => ({ ...prev, [pathId]: outcome }));
  }, []);

  // A switch write returns the redrawn path list; swap it in so the row's
  // badge and reason follow what is actually stored.
  const onPaths = useCallback((paths) => {
    setStatus((prev) => (prev ? {
      ...prev,
      paths,
      covered_count: paths.filter((p) => p.covered).length,
    } : prev));
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [load]);

  if (error) {
    return (
      <Alert status="error"><AlertIcon />Could not load status: {error}</Alert>
    );
  }
  if (!status) return <Spinner />;

  const tested = Object.keys(verdicts).length;
  const outcomes = Object.values(verdicts);
  const passedCount = outcomes.filter((o) => o === "passed").length;
  const unprotectedCount = outcomes.filter((o) => o === "unprotected").length;
  // A NOT PROTECTED path (images or audio to an outside service) is a known
  // boundary, not a broken scrub — on its own it is a yellow note, not an
  // alarm. Red is reserved for a covered path that leaked or a clinic that
  // switched de-identification off.
  const genuineFailures = tested - passedCount - unprotectedCount;
  const summaryTone = genuineFailures > 0 ? "red" : unprotectedCount > 0 ? "yellow" : "green";
  const summaryLabel = { red: "ATTENTION", yellow: "COVERED PATHS CLEAR", green: "ALL CLEAR" }[summaryTone];

  return (
    <VStack align="stretch" spacing={5}>
      <StatusBanner status={status} />
      <Demo demo={status.demo} />
      <Divider />
      <Box>
        <HStack mb={1} justify="space-between">
          <HStack>
            <Text fontSize="lg" fontWeight="bold">AI paths</Text>
            <Badge colorScheme="green">{status.covered_count} covered</Badge>
            <Badge colorScheme="orange">
              {status.total_paths - status.covered_count} not covered
            </Badge>
          </HStack>
          <Button
            size="sm"
            colorScheme="blue"
            data-testid="test-all"
            onClick={() => { setVerdicts({}); setRunAllToken((n) => n + 1); }}
          >
            {Object.keys(cachedTests).length ? "Re-test All" : "Test All"}
          </Button>
        </HStack>

        {tested > 0 && (
          <Box
            mb={3} px={3} py={2} borderRadius="md" borderWidth="2px"
            bg={`${summaryTone}.50`}
            borderColor={`${summaryTone}.400`}
            data-testid="run-all-summary"
            data-tone={summaryTone}
          >
            <HStack>
              <Badge colorScheme={summaryTone} fontSize="sm" px={2}>
                {summaryLabel}
              </Badge>
              <Text fontSize="sm" fontWeight="bold">
                {passedCount} of {tested} tested paths passed
              </Text>
            </HStack>
            {summaryTone === "yellow" && (
              <Text fontSize="xs" color="gray.700" mt={1}>
                Every path our scrub covers passed. The {unprotectedCount} that did not
                {unprotectedCount === 1 ? " is" : " are"} marked NOT PROTECTED — {unprotectedCount === 1 ? "it sends" : "they send"} images
                or audio to a service outside the app, where text scrubbing cannot apply.
                Nothing here is broken.
              </Text>
            )}
            {summaryTone === "red" && (
              <Text fontSize="xs" color="gray.700" mt={1}>
                A red FAIL is a genuine problem: on a covered path the scrub broke; on a
                SWITCHED OFF path a clinic has turned de-identification off (the row
                names the schema — flip its switch). Paths marked NOT PROTECTED are the
                known image/audio boundary and are not counted against you.
              </Text>
            )}
          </Box>
        )}
        <Text fontSize="sm" color="gray.600" mb={2}>
          Everything that sends data to an AI model. The uncovered ones send images or
          audio, which cannot be scrubbed as text.
        </Text>
        {status.paths.map((p) => (
          <PathRow
            key={p.id || p.name}
            path={p}
            runTest={runTest}
            runAllToken={runAllToken}
            onVerdict={onVerdict}
            cachedEntry={cachedTests[p.id]}
            setMasterNoteDeid={setMasterNoteDeid}
            onPaths={onPaths}
          />
        ))}
      </Box>
    </VStack>
  );
}
